import React, { useState, useEffect } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import API_CONFIG from '../config/api';
import { SubscriptionsData, ChartDataItem, SubscriptionUser } from '../types/api';
import { requestManager } from '../utils/requestManager';
import { cacheEventManager } from '../utils/cacheEventManager';

interface SubscriptionsProps {
    timeRange: string;
}

const Subscriptions: React.FC<SubscriptionsProps> = ({ timeRange }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [subscriptionsData, setSubscriptionsData] = useState<SubscriptionsData | null>(null);

    // State for collapsible sections
    const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({
        charts: false,
        detailedBreakdown: false,
        summaryTable: false,
        activeUsers: false,
        inactiveUsers: false
    });

    // Toggle function for collapsible sections
    const toggleSection = (sectionKey: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // API Helper Function with request deduplication
    const apiCall = async (endpoint: string) => {
        try {
            const url = `${API_CONFIG.BASE_URL}${endpoint}?period=${timeRange}`;
            const data = await requestManager.makeRequest(url);

            if (!data.success) throw new Error(data.message || 'API call failed');
            return data.data;
        } catch (error) {
            console.error(`API Error for ${endpoint}:`, error);
            throw error;
        }
    };

    const fetchSubscriptionsData = async () => {
        try {
            setLoading(true);
            const data = await apiCall(API_CONFIG.ENDPOINTS.ANALYTICS.SUBSCRIPTIONS);
            setSubscriptionsData(data);
        } catch (error) {
            console.error('Error fetching subscriptions data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscriptionsData();
    }, [timeRange]);

    // Listen for cache clear events to auto-refresh data
    useEffect(() => {
        const unsubscribe = cacheEventManager.subscribe(() => {
            fetchSubscriptionsData();
        });

        return unsubscribe;
    }, [timeRange]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading subscriptions data...</p>
                </div>
            </div>
        );
    }

    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

    // Function to get color based on plan type
    const getPlanColor = (planType: string) => {
        return planType === 'Plus' ? '#3B82F6' : '#10B981'; // Blue for Plus, Green for Pro
    };

    // Prepare data for charts - use individual plan counts for detailed breakdown
    const planData = subscriptionsData ? [
        { name: 'Plus Monthly', value: subscriptionsData.plus_monthly || 0, type: 'Plus' },
        { name: 'Plus 4 Months', value: subscriptionsData.plus_4months || 0, type: 'Plus' },
        { name: 'Plus Yearly', value: subscriptionsData.plus_yearly || 0, type: 'Plus' },
        { name: 'Pro Monthly', value: subscriptionsData.pro_monthly || 0, type: 'Pro' },
        { name: 'Pro 4 Months', value: subscriptionsData.pro_4months || 0, type: 'Pro' },
        { name: 'Pro Yearly', value: subscriptionsData.pro_yearly || 0, type: 'Pro' }
    ].filter(item => item.value > 0) : [];

    // Prepare data for subscription summary - use total counts instead of individual plan counts
    const summaryData = subscriptionsData ? [
        { name: 'Plus Total', value: subscriptionsData.total_plus_subscriptions || 0, type: 'Plus' },
        { name: 'Pro Total', value: subscriptionsData.total_pro_subscriptions || 0, type: 'Pro' }
    ].filter(item => item.value > 0) : [];

    const categoryData = subscriptionsData ? [
        { name: 'Plus Plans', value: subscriptionsData.total_plus_subscriptions || 0, color: '#3B82F6' },
        { name: 'Pro Plans', value: subscriptionsData.total_pro_subscriptions || 0, color: '#10B981' }
    ] : [];

    const barData = planData.map(item => ({
        ...item,
        name: item.name.length > 12 ? item.name.substring(0, 12) + '...' : item.name
    }));

    // Collapsible Header Component
    const CollapsibleHeader: React.FC<{
        title: string;
        count?: number;
        color: string;
        isCollapsed: boolean;
        onToggle: () => void;
    }> = ({ title, count, color, isCollapsed, onToggle }) => (
        <div
            className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 -m-2 transition-colors"
            onClick={onToggle}
        >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                <div className={`w-4 h-4 ${color} rounded-full mr-2`}></div>
                {title} {count !== undefined && `(${count})`}
            </h3>
            <div className="flex items-center">
                <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''
                        }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </div>
    );

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Content */}
            <main className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
                {/* Summary Cards - Single Row Layout */}
                <div className="grid grid-cols-5 gap-4 mb-8">
                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Total Subscriptions</p>
                        <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">
                            {subscriptionsData?.total_subscriptions?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Active Subscriptions</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                            {subscriptionsData?.total_active_subscriptions?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Expired Subscriptions</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                            {subscriptionsData?.inactive_users?.length?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Plus Plans</p>
                        <p className="text-2xl font-bold text-blue-600 mt-1">
                            {subscriptionsData?.total_plus_subscriptions?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Pro Plans</p>
                        <p className="text-2xl font-bold text-orange-600 mt-1">
                            {subscriptionsData?.total_pro_subscriptions?.toLocaleString() || 0}
                        </p>
                    </div>
                </div>

                {/* Charts - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Subscription Charts"
                        isCollapsed={collapsedSections.charts}
                        onToggle={() => toggleSection('charts')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.charts && (
                        <div className="flex gap-6 mt-6">
                            {/* Category Pie Chart - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Plan Categories</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={categoryData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                                            >
                                                {categoryData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Individual Plans Bar Chart - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Individual Plan Counts</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="name"
                                                angle={-45}
                                                textAnchor="end"
                                                height={100}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), 'Subscriptions']}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                            <Bar dataKey="value">
                                                {barData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={getPlanColor(entry.type)} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Detailed Breakdown - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Plan Breakdown Details"
                        isCollapsed={collapsedSections.detailedBreakdown}
                        onToggle={() => toggleSection('detailedBreakdown')}
                        color="bg-blue-500"
                    />

                    {!collapsedSections.detailedBreakdown && (
                        <div className="flex gap-6 mt-6">
                            {/* Plus Plans - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                    Plus Plans Breakdown
                                </h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Monthly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {subscriptionsData?.plus_monthly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">4 Months</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {subscriptionsData?.plus_4months || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Yearly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {subscriptionsData?.plus_yearly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <span className="text-blue-600 dark:text-blue-400 font-medium">Total Plus</span>
                                        <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                            {subscriptionsData?.total_plus_subscriptions || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Pro Plans - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                    Pro Plans Breakdown
                                </h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Monthly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {subscriptionsData?.pro_monthly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">4 Months</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {subscriptionsData?.pro_4months || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Yearly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {subscriptionsData?.pro_yearly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                        <span className="text-green-600 dark:text-green-400 font-medium">Total Pro</span>
                                        <span className="text-xl font-bold text-green-600 dark:text-green-400">
                                            {subscriptionsData?.total_pro_subscriptions || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Summary Table */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Subscription Summary"
                        count={summaryData.length}
                        isCollapsed={collapsedSections.summaryTable}
                        onToggle={() => toggleSection('summaryTable')}
                        color="bg-emerald-500"
                    />

                    {!collapsedSections.summaryTable && (
                        <div className="overflow-x-auto mt-6">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead>
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Plan Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Duration
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Count
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Percentage
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                    {summaryData.map((plan, index) => {
                                        const total = subscriptionsData?.total_subscriptions || 1;
                                        const percentage = ((plan.value / total) * 100).toFixed(1);

                                        return (
                                            <tr key={plan.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div
                                                            className={`w-4 h-4 rounded-full mr-3 ${plan.type === 'Plus' ? 'bg-blue-500' : 'bg-green-500'
                                                                }`}
                                                        ></div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {plan.type}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    All Durations
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {plan.value.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {percentage}%
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Active Users Details */}
                {subscriptionsData?.active_users && subscriptionsData.active_users.length > 0 && (
                    <div className="glass-card p-6 mb-6">
                        <CollapsibleHeader
                            title="Active Subscriptions"
                            count={subscriptionsData.active_users.length}
                            isCollapsed={collapsedSections.activeUsers}
                            onToggle={() => toggleSection('activeUsers')}
                            color="bg-green-500"
                        />

                        {!collapsedSections.activeUsers && (
                            <div className="mt-6">
                                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Plan Type
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Ends On
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Started On
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {subscriptionsData.active_users
                                                .slice()
                                                .sort((a, b) => new Date(b.transaction_timestamp || 0).getTime() - new Date(a.transaction_timestamp || 0).getTime())
                                                .map((user: SubscriptionUser, index: number) => (
                                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                    <div className="h-10 w-10 rounded-full bg-green-400 flex items-center justify-center text-white font-semibold text-sm">
                                                                        {user.email?.charAt(0).toUpperCase() || 'U'}
                                                                    </div>
                                                                </div>
                                                                <div className="ml-4">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {user.email || 'N/A'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {user.user_id || 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {user.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                                {user.subscription_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                            {user.ending_timestamp ? new Date(user.ending_timestamp).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'N/A'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                            {user.transaction_timestamp ? new Date(user.transaction_timestamp).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'N/A'}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Inactive Users Details */}
                {subscriptionsData?.inactive_users && subscriptionsData.inactive_users.length > 0 && (
                    <div className="glass-card p-6 mb-6">
                        <CollapsibleHeader
                            title="Expired Subscriptions"
                            count={subscriptionsData.inactive_users.length}
                            isCollapsed={collapsedSections.inactiveUsers}
                            onToggle={() => toggleSection('inactiveUsers')}
                            color="bg-red-500"
                        />

                        {!collapsedSections.inactiveUsers && (
                            <div className="mt-6">
                                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Plan Type
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Ended On
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Started On
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {subscriptionsData.inactive_users.map((user: SubscriptionUser, index: number) => (
                                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="flex-shrink-0 h-10 w-10">
                                                                <div className="h-10 w-10 rounded-full bg-red-400 flex items-center justify-center text-white font-semibold text-sm">
                                                                    {user.email?.charAt(0).toUpperCase() || 'U'}
                                                                </div>
                                                            </div>
                                                            <div className="ml-4">
                                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                    {user.email || 'N/A'}
                                                                </div>
                                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                    {user.user_id || 'N/A'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {user.email}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                                            {user.subscription_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                        {user.ending_timestamp ? new Date(user.ending_timestamp).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric'
                                                        }) : 'N/A'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                        {user.transaction_timestamp ? new Date(user.transaction_timestamp).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric'
                                                        }) : 'N/A'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default Subscriptions;